// components/common/tech-term-system.tsx - Auth Integration

"use client";

import { Book, HelpCircle, Lightbulb, Zap } from "lucide-react";
import React, { createContext, ReactNode, useContext, useState } from "react";

import { type KnowledgeLevel } from "./knowledgeLevel";
import { knowledgeLevelStore } from "./knowledgeLevelStore";
// import { useAuth } from '@/hooks/useAuth'; // Integrate with your auth system

// Define levels
export type { KnowledgeLevel } from "./knowledgeLevel";

// v2

// ### CODE CHANGES START ###
// Updated TermExplanation interface to match our implementation
export interface TermExplanation {
  // Direct definitions by knowledge level
  beginner: string;
  intermediate: string;
  advanced: string;
  // Optional URL for more information
  learnMoreUrl?: string;
  // Optional categories for filtering
  categories?: string[];
  // Optional related terms
  relatedTerms?: string[];
}

// Context to store and manage user knowledge level
interface UserLevelContextType {
  userLevel: KnowledgeLevel;
  setUserLevel: (level: KnowledgeLevel) => void;
  glossary: Record<string, TermExplanation>;
}

// Default context value
const defaultContextValue: UserLevelContextType = {
  userLevel: "beginner",
  setUserLevel: () => {},
  glossary: {},
};

const UserLevelContext =
  createContext<UserLevelContextType>(defaultContextValue);

export const UserLevelProvider: React.FC<{
  children: ReactNode;
  initialGlossary?: Record<string, TermExplanation>;
}> = ({ children, initialGlossary = {} }) => {
  const userLevel = knowledgeLevelStore.useStore();
  const [glossary] = useState<Record<string, TermExplanation>>(initialGlossary);

  return (
    <UserLevelContext.Provider
      value={{ userLevel, setUserLevel: knowledgeLevelStore.set, glossary }}
    >
      {children}
    </UserLevelContext.Provider>
  );
};

// Hook to use the user level
export const useUserLevel = () => useContext(UserLevelContext);

// Simplified Tech term component with tooltip
export const TechTerm: React.FC<{ term: string; children?: ReactNode }> = ({
  term,
  children,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const { userLevel, glossary } = useUserLevel();

  // Look up in glossary
  const definition = glossary[term];

  if (!definition) {
    return <span>{children || term}</span>;
  }

  return (
    <span className="relative inline-block group">
      <span
        className="inline-flex items-center cursor-help"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={() => setShowTooltip(!showTooltip)}
      >
        {children || term}
        <HelpCircle className="inline ml-1 text-blue-400" size={14} />
      </span>

      {showTooltip && (
        <div className="absolute z-50 left-0 bottom-full mb-2 w-64 p-3 rounded bg-gray-800 border border-gray-700 text-gray-300 text-sm shadow-xl">
          <div className="font-bold text-white mb-1">{term}</div>
          <div>{definition[userLevel]}</div>
          {definition.learnMoreUrl && (
            <a
              href={definition.learnMoreUrl}
              className="text-blue-400 text-xs mt-2 inline-block"
            >
              Learn more →
            </a>
          )}
          <div className="absolute w-3 h-3 bg-gray-800 rotate-45 border-r border-b border-gray-700 left-5 -bottom-1.5" />
        </div>
      )}
    </span>
  );
};

// Process tech terms - improved for better term detection (case insensitive)
export const ProcessTechTerms = ({ text }: { text: string }) => {
  const { glossary } = useUserLevel();

  if (!text || typeof text !== "string") {
    return null;
  }

  const termList = Object.keys(glossary);

  // If no glossary terms, just return the text
  if (termList.length === 0) {
    return <>{text}</>;
  }

  // Function to check if a substring at a position is a term (case insensitive)
  const isTermAtPosition = (str: string, pos: number, term: string) => {
    // Check if term starts at position (case insensitive)
    const potentialMatch = str.substring(pos, pos + term.length);
    if (potentialMatch.toLowerCase() !== term.toLowerCase()) {
      return false;
    }

    // Check if term is a whole word (has word boundaries)
    const beforeChar = pos === 0 ? " " : str[pos - 1];
    const afterChar =
      pos + term.length >= str.length ? " " : str[pos + term.length];
    const wordBoundaryChars = " .,;:!?()[]{}\"'<>-_\n\t";

    return (
      wordBoundaryChars.includes(beforeChar) &&
      wordBoundaryChars.includes(afterChar)
    );
  };

  // Sort terms by length (descending) for better matching
  const sortedTerms = [...termList].sort((a, b) => b.length - a.length);

  // Start processing
  const result = [];
  let lastIndex = 0;

  for (let i = 0; i < text.length; i++) {
    // Check if any term starts at this position
    const matchingTerm = sortedTerms.find((term) =>
      isTermAtPosition(text, i, term),
    );

    if (matchingTerm) {
      // Add text before the term
      if (i > lastIndex) {
        result.push(text.substring(lastIndex, i));
      }

      // Add the term as a TechTerm component
      const exactMatch = text.substring(i, i + matchingTerm.length);
      result.push(
        <TechTerm key={`term-${i}`} term={matchingTerm}>
          {exactMatch}
        </TechTerm>,
      );

      // Skip ahead
      lastIndex = i + matchingTerm.length;
      i = lastIndex - 1; // -1 because the loop will increment i
    }
  }

  // Add remaining text
  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex));
  }

  return <>{result}</>;
};

// Add more terms with consistent casing
export const techTermsDatabase: Record<string, TermExplanation> = {
  "Agentic AI": {
    beginner:
      "AI systems with multiple specialized 'agents' that work together to handle tasks independently.",
    intermediate:
      "AI systems composed of multiple specialized 'agents' that can perform tasks autonomously on behalf of users.",
    advanced:
      "Multi-agent AI architecture where specialized components work together to perform complex tasks with defined responsibilities and communication protocols.",
    learnMoreUrl: "/learn/ai-concepts/agentic-ai",
    categories: ["AI Architecture", "Automation"],
    relatedTerms: ["LLM", "AI Agents", "Autonomous Systems"],
  },
  LLM: {
    beginner:
      "AI systems that understand and generate human language (like Gemini, GPT, etc.).",
    intermediate:
      "Powerful AI models (like Gemini, GPT) that understand and generate text.",
    advanced:
      "Neural network-based models trained on vast text corpora that can understand context, generate coherent text, and perform a wide range of language tasks.",
    learnMoreUrl: "/learn/ai-concepts/large-language-models",
    categories: ["AI Models", "Natural Language Processing"],
    relatedTerms: [
      "Transformer",
      "Natural Language Processing",
      "Foundation Model",
    ],
  },
  "Vector Database": {
    beginner:
      "A special database that understands the meaning of your data, not just exact matches.",
    intermediate:
      "A database designed to store and search vector embeddings efficiently, allowing meaning-based searches.",
    advanced:
      "A database optimized for storing high-dimensional vector embeddings and performing approximate nearest neighbor (ANN) searches using similarity metrics.",
    learnMoreUrl: "/learn/ai-concepts/vector-databases",
  },
  "Embedding Model": {
    beginner:
      "AI that converts words and data into numerical values that capture meaning.",
    intermediate:
      "A pre-trained neural network that transforms text or data into numerical vector representations.",
    advanced:
      "A specialized neural network architecture that maps input data to dense vector representations in a continuous vector space, preserving semantic relationships.",
    learnMoreUrl: "/learn/ai-concepts/embedding-models",
  },
  "Semantic Search": {
    beginner: "Search that understands what you mean, not just what you type.",
    intermediate:
      "Search capability that finds results based on the meaning of your query rather than exact keyword matches.",
    advanced:
      "A retrieval method that uses vector similarity to match queries with content based on semantic relevance rather than lexical matching.",
    learnMoreUrl: "/learn/ai-concepts/semantic-search",
  },
};
// ### CODE CHANGES FINISH ###

// /v1// // Define term explanation structure with multi-language support
// export interface TermExplanation {
//   en: {
//     beginner: string;
//     intermediate: string;
//     advanced: string;
//   };
//   pt?: {
//     beginner: string;
//     intermediate: string;
//     advanced: string;
//   };
//   // Add more languages as needed
//   learnMoreUrl?: {
//     en: string;
//     pt?: string;
//     // Add more languages as needed
//   };
// }

// // Context to store and manage user knowledge level
// interface UserLevelContextType {
//   userLevel: KnowledgeLevel;
//   setUserLevel: (level: KnowledgeLevel) => void;
//   glossary: Record<string, TermExplanation>;
//   language: string;
//   setLanguage: (lang: string) => void;
// }

// const UserLevelContext = createContext<UserLevelContextType>({
//   userLevel: 'beginner',
//   setUserLevel: () => {},
//   glossary: {},
//   language: 'en',
//   setLanguage: () => {}
// });

// const useAuth = () => {
//   return {
//     user: {
//       id: "mock-user-123",
//       userid: "mock-user-123", // Some implementations use userid instead of id
//       name: "Test User",
//       email: "test@example.com",
//       preferences: {
//         knowledgeLevel: "beginner",
//         language: "en"
//       }
//     },
//     isLoading: false,
//     isAuthenticated: true,
//     login: () => Promise.resolve(),
//     logout: () => Promise.resolve(),
//     register: () => Promise.resolve()
//   };
// };

// // Provider component
// export const UserLevelProvider: React.FC<{ children: ReactNode, initialGlossary?: Record<string, TermExplanation> }> = ({
//   children,
//   initialGlossary = {}
// }) => {
//   const { user, isLoading } = useAuth();
//   const [userLevel, setUserLevel] = useState<KnowledgeLevel>('beginner');
//   const [glossary, setGlossary] = useState<Record<string, TermExplanation>>(initialGlossary);
//   const [language, setLanguage] = useState<string>('en');

//   // Load saved level on initial render or when user auth state changes
//   useEffect(() => {
//     if (isLoading) return;

//     if (user) {
//       // If user is logged in, try to fetch their preference from your backend
//       // This is a placeholder - implement with your actual API call
//       fetch(`/api/user-preferences/${user.userid}`)
//         .then(res => res.json())
//         .then(data => {
//           if (data.knowledgeLevel && ['beginner', 'intermediate', 'advanced'].includes(data.knowledgeLevel)) {
//             setUserLevel(data.knowledgeLevel as KnowledgeLevel);
//           }
//           if (data.language) {
//             setLanguage(data.language);
//           }
//         })
//         .catch(() => {
//           // Fall back to localStorage if API fails
//           const savedLevel = localStorage.getItem('userKnowledgeLevel');
//           if (savedLevel && ['beginner', 'intermediate', 'advanced'].includes(savedLevel)) {
//             setUserLevel(savedLevel as KnowledgeLevel);
//           }
//         });
//     } else {
//       // Not logged in, use localStorage
//       const savedLevel = localStorage.getItem('userKnowledgeLevel');
//       if (savedLevel && ['beginner', 'intermediate', 'advanced'].includes(savedLevel)) {
//         setUserLevel(savedLevel as KnowledgeLevel);
//       }

//       const savedLanguage = localStorage.getItem('userLanguage');
//       if (savedLanguage) {
//         setLanguage(savedLanguage);
//       }
//     }
//   }, [user, isLoading]);

//   // Save level when it changes
//   useEffect(() => {
//     localStorage.setItem('userKnowledgeLevel', userLevel);

//     // If user is logged in, save to your backend too
//     if (user) {
//       fetch(`/api/user-preferences/${user.id}`, {
//         method: 'PATCH',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({ knowledgeLevel: userLevel })
//       }).catch(err => console.error('Failed to save user level', err));
//     }
//   }, [userLevel, user]);

//   // Save language when it changes
//   useEffect(() => {
//     localStorage.setItem('userLanguage', language);

//     // If user is logged in, save to your backend too
//     if (user) {
//       fetch(`/api/user-preferences/${user.id}`, {
//         method: 'PATCH',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({ language })
//       }).catch(err => console.error('Failed to save language preference', err));
//     }
//   }, [language, user]);

//   return (
//     <UserLevelContext.Provider value={{
//       userLevel,
//       setUserLevel,
//       glossary,
//       language,
//       setLanguage
//     }}>
//       {children}
//     </UserLevelContext.Provider>
//   );
// };

// // Hook to use the user level
// export const useUserLevel = () => useContext(UserLevelContext);

// // Tech term component with tooltip - improved mobile styling
// export const TechTerm: React.FC<{ term: string; children?: ReactNode }> = ({ term, children }) => {
//   const [showTooltip, setShowTooltip] = useState(false);
//   const { userLevel, glossary, language } = useUserLevel();

//   // Look up in glossary
//   const definition = glossary[term];

//   if (!definition) {
//     return <span>{children || term}</span>;
//   }

//   const langKey = (language in definition) ? language as keyof typeof definition : 'en';

//   if (!definition[langKey]) {
//     return <span>{children || term}</span>;
//   }

//   return (
//     <span className="relative inline-block">
//       <span
//         className="text-blue-400 border-b border-dotted border-blue-400 cursor-help inline-flex items-center"
//         onMouseEnter={() => setShowTooltip(true)}
//         onMouseLeave={() => setShowTooltip(false)}
//         onClick={() => setShowTooltip(!showTooltip)}
//       >
//         {children || term}
//         <HelpCircle className="inline ml-1" size={14} />
//       </span>

//     {showTooltip && (
//     <div className="fixed inset-x-4 bottom-4 md:absolute md:inset-auto md:left-1/2 md:bottom-full md:mb-2 md:transform md:-translate-x-1/2 p-3 rounded bg-gray-950 border border-gray-700 text-gray-300 text-sm shadow-xl z-50 md:w-72">
//         <div className="font-bold text-white mb-1">{term}</div>
//         <div>{(definition[langKey] as any)[userLevel]}</div>
//         {definition.learnMoreUrl && definition.learnMoreUrl[langKey as keyof typeof definition.learnMoreUrl] && (
//         <a href={definition.learnMoreUrl[langKey as keyof typeof definition.learnMoreUrl]} className="text-blue-400 text-xs mt-2 inline-block">
//             Learn more →
//         </a>
//         )}
//         <div className="hidden md:block absolute w-3 h-3 bg-gray-950 rotate-45 border-r border-b border-gray-700 left-1/2 -bottom-1.5 -ml-1.5"></div>
//     </div>
//     )}
//     </span>
//   );
// };

// // Floating user level selector component
export const UserLevelFloatingSelector: React.FC = () => {
  const { userLevel, setUserLevel } = useUserLevel();
  const [isOpen, setIsOpen] = useState(false);

  const getLevelIcon = () => {
    switch (userLevel) {
      case "beginner":
        return <Book size={18} />;
      case "intermediate":
        return <Lightbulb size={18} />;
      case "advanced":
        return <Zap size={18} />;
      default:
        return <Book size={18} />;
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-40">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 transition-colors"
        aria-label="Change knowledge level"
      >
        {getLevelIcon()}
      </button>

      {isOpen && (
        <div className="absolute bottom-16 right-0 bg-gray-900 p-2 rounded-lg shadow-xl border border-gray-700 w-40">
          <div className="text-xs text-gray-400 mb-2 px-2">Knowledge Level</div>
          <button
            onClick={() => {
              setUserLevel("beginner");
              setIsOpen(false);
            }}
            className={`flex items-center w-full text-left px-3 py-2 rounded ${userLevel === "beginner" ? "bg-blue-600 text-white" : "hover:bg-gray-800 text-gray-300"}`}
          >
            <Book size={16} className="mr-2" /> Beginner
          </button>
          <button
            onClick={() => {
              setUserLevel("intermediate");
              setIsOpen(false);
            }}
            className={`flex items-center w-full text-left px-3 py-2 rounded ${userLevel === "intermediate" ? "bg-blue-600 text-white" : "hover:bg-gray-800 text-gray-300"}`}
          >
            <Lightbulb size={16} className="mr-2" /> Intermediate
          </button>
          <button
            onClick={() => {
              setUserLevel("advanced");
              setIsOpen(false);
            }}
            className={`flex items-center w-full text-left px-3 py-2 rounded ${userLevel === "advanced" ? "bg-blue-600 text-white" : "hover:bg-gray-800 text-gray-300"}`}
          >
            <Zap size={16} className="mr-2" /> Advanced
          </button>
        </div>
      )}
    </div>
  );
};

// export const ProcessTechTerms = ({ text }: { text: string }) => {
//   const { glossary } = useUserLevel();
//   const termList = Object.keys(glossary);

//   // Sort terms by length (descending) to match longer terms first
//   const sortedTerms = [...termList].sort((a, b) => b.length - a.length);

//   let lastIndex = 0;
//   const segments = [];
//   let currentText = text;

//   // Find and replace tech terms with TechTerm components
//   sortedTerms.forEach((term) => {
//     const regex = new RegExp(`\\b${term}\\b`, "gi");
//     let match;
//     let offset = 0;

//     while ((match = regex.exec(currentText)) !== null) {
//       const matchIndex = match.index;

//       // Add text before the match
//       if (matchIndex > lastIndex) {
//         segments.push(currentText.substring(lastIndex, matchIndex));
//       }

//       // Add the TechTerm component
//       segments.push(
//         <TechTerm key={`${term}-${matchIndex}`} term={term}>
//           {match[0]}
//         </TechTerm>
//       );

//       lastIndex = matchIndex + term.length;
//     }
//   });

//   // Add any remaining text
//   if (lastIndex < currentText.length) {
//     segments.push(currentText.substring(lastIndex));
//   }

//   return <>{segments.length > 0 ? segments : text}</>;
// };

// // JSON structure for tech terms database
// export const techTermsDatabase = {
//   "Agentic AI": {
//     categories: ["AI Architecture", "Automation"],
//     definitions: {
//       en: {
//         beginner:
//           "AI systems with multiple specialized AI 'agents' that work together to handle tasks independently.",
//         intermediate:
//           "AI systems composed of multiple specialized 'agents' that can perform tasks autonomously on behalf of users.",
//         advanced:
//           "Multi-agent AI architecture where specialized components work together to perform complex tasks with defined responsibilities and communication protocols.",
//       },
//     },
//     relatedTerms: ["LLM", "AI Agents", "Autonomous Systems"],
//     learnMoreUrl: {
//       en: "/learn/ai-concepts/agentic-ai",
//     },
//   },
//   LLM: {
//     categories: ["AI Models", "Natural Language Processing"],
//     definitions: {
//       en: {
//         beginner:
//           "AI systems that understand and generate human language (like Gemini, GPT, etc.).",
//         intermediate:
//           "Powerful AI models (like Gemini, GPT) that understand and generate text.",
//         advanced:
//           "Neural network-based models trained on vast text corpora that can understand context, generate coherent text, and perform a wide range of language tasks.",
//       },
//     },
//     relatedTerms: [
//       "Transformer",
//       "Natural Language Processing",
//       "Foundation Model",
//     ],
//     learnMoreUrl: {
//       en: "/learn/ai-concepts/large-language-models",
//     },
//   },
//   // Add more terms as needed
// };
